/* eslint-disable max-lines */
import { Card, Flex, Stack, Text } from '@mantine/core';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import icInfo2 from '../../assets/ic-info2.svg';
import icInfoSolid from '../../assets/ic-info-solid.svg';
import icLocation2 from '../../assets/ic-location2.svg';
import Button from '../../components/elements/Button';
import CustomerSearchForm from '../../components/forms/CustomerSearchForm';
import IcUser from '../../components/icons/IcUser';
import { fetchProvinces, fetchSessionProtection } from '../../components/layouts/PageBase/actions';
import { AppContext, ToastrContext } from '../../contexts';
import { getCustomerTypeBadge } from '../../utils/customerTypeBadge';
import PeriodNotice from './PeriodNotice';
import {
  actionUpdateProvinceTransactionRestriction,
  clearRedux,
  fetchProvinceTransactionRestriction,
} from './action';
import ProvinceTransactionRestrictionModal from './modal';
import styles from './styles.scoped.css';
import { getRestrictionMonthInfo } from './utils/historyDateTime';
import { sortProvinceOptions } from './utils/provinceOptions';

const RESTRICTED_STATUS_PROVINCE = [2, 3];

const normalizeProvince = (province) => {
  if (typeof province === 'string') {
    return {
      label: province,
      value: province,
    };
  }

  return {
    label: province?.province || province?.provinceName || province?.name || '-',
    value: province?.provinceId || province?.id || province?.value || province?.province || '',
  };
};

function ActiveProvinceBadge() {
  return (
    <span className={styles.provinceStatusBadge}>
      Provinsi Transaksi Dibatasi
    </span>
  );
}

function MonthRestrictionNotice({ monthLabel, monthName, year }) {
  return (
    <PeriodNotice>
      Provinsi transaksi ini berlaku untuk{' '}
      <strong>bulan ini ({monthLabel})</strong>. Pelanggan dapat bertransaksi di
      provinsi tersebut hingga <strong>akhir bulan {monthName} {year}</strong>.
    </PeriodNotice>
  );
}

MonthRestrictionNotice.propTypes = {
  monthLabel: PropTypes.string.isRequired,
  monthName: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
};

function CustomerTypeBadge({ sourceTypeId }) {
  const badge = getCustomerTypeBadge(sourceTypeId);

  return (
    <span
      className={styles.customerTypeBadge}
      style={{
        backgroundColor: badge.backgroundColor,
        color: badge.textColor,
      }}
    >
      {badge.label}
    </span>
  );
}

function DetailRow({ children, label }) {
  return (
    <div className={styles.detailRow}>
      <div className={styles.detailLabel}>{label}</div>
      <div className={styles.detailValue}>{children}</div>
    </div>
  );
}

CustomerTypeBadge.propTypes = {
  sourceTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default function ProvinceTransactionRestriction() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useContext(AppContext);
  const { setToastr } = useContext(ToastrContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { dataProvinceTransactionRestriction, updateProvinceTransactionRestriction } = useSelector(
    (state) => state.provinceTransactionRestriction
  );
  const { dataProvinces } = useSelector((state) => state.pageBase);

  const searchResult = dataProvinceTransactionRestriction?.data || {};
  const isProvinceRestricted = RESTRICTED_STATUS_PROVINCE.includes(
    searchResult?.statusProvince
  );
  const restrictionMonthInfo = useMemo(
    () => getRestrictionMonthInfo(searchResult),
    [searchResult]
  );
  const sortedProvinceOptions = useMemo(
    () => sortProvinceOptions(dataProvinces),
    [dataProvinces]
  );
  const activeProvinces = useMemo(
    () => (searchResult?.provinces || []).map(normalizeProvince).filter((item) => item.label),
    [searchResult?.provinces]
  );
  const isNotice = searchResult?.isNotice ?? false;
  const customerTypes = searchResult?.customerTypes || [];
  const disableWhitelist = Boolean(searchResult?.disableWhitelist);
  const disableWhitelistCount =
    searchResult?.disableWhitelistCount ||
    searchResult?.whitelistRestrictionCount ||
    searchResult?.actionCount ||
    searchResult?.monthlyRestrictionCount ||
    3;
  const disableWhitelistMessage =
    searchResult?.disableWhitelistMessage ||
    searchResult?.disableWhitelistNote ||
    searchResult?.whitelistRestrictionMessage ||
    `Sudah Hapus/Set Provinsi Transaksi ${disableWhitelistCount} kali di bulan ini.`;

  useEffect(() => {
    dispatch(clearRedux('dataProvinceTransactionRestriction'));
    dispatch(clearRedux('updateProvinceTransactionRestriction'));
    dispatch(fetchSessionProtection());
    dispatch(fetchProvinces());
    setBreadcrumbs(['Pembatasan Transaksi Berdasarkan Provinsi']);
  }, []);

  const handleSubmitSearch = (values) => {
    dispatch(fetchProvinceTransactionRestriction(values.nationalityId, true));
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitRestriction = (formData, actionType) => {
    dispatch(
      actionUpdateProvinceTransactionRestriction(formData, (response) => {
        if (response.success) {
          setToastr({
            desc:
              actionType === 'delete'
                ? 'Berhasil menghapus pembatasan provinsi transaksi pelanggan.'
                : 'Berhasil memperbarui whitelist provinsi transaksi pelanggan.',
            type: 'success',
            visible: true,
          });
          setIsModalOpen(false);
          dispatch(fetchProvinceTransactionRestriction(searchResult.nationalityIdPlan, true));
          return;
        }

        setToastr({
          desc: response.message || 'Gagal memperbarui pembatasan provinsi transaksi pelanggan.',
          type: 'danger',
          visible: true,
        });
      })
    );
  };

  const handleGoToDetail = () => {
    navigate(`/province-transaction-restriction/detail/${searchResult.nationalityIdPlan}`);
  };

  const renderResultSearch = () => {
    return (
      <Card mt={30} radius="md" withBorder>
        <div className={styles.resultCard}>
          <Flex align="center" className={styles.resultHeader} gap="sm" wrap="wrap">
            <IcUser className={styles.profileIcon} />
            <Text className={styles.customerName}>{searchResult.name}</Text>
            {isProvinceRestricted && <ActiveProvinceBadge />}
          </Flex>

          {isProvinceRestricted && isNotice && (
            <MonthRestrictionNotice
              monthLabel={restrictionMonthInfo.monthLabel}
              monthName={restrictionMonthInfo.monthName}
              year={restrictionMonthInfo.yearMonth.split('-')[0]}
            />
          )}

          <Stack className={styles.detailList}>
            <DetailRow label="NIK">{searchResult.nationalityId}</DetailRow>
            <DetailRow label="Jenis Pelanggan">
              <div className={styles.customerTypeList}>
                {customerTypes.length > 0 ? (
                  customerTypes.map((type) => (
                    <div
                      className={styles.customerTypeItem}
                      key={`${type.name}-${type.sourceTypeId}`}
                    >
                      <span>{type.name}</span>
                      <CustomerTypeBadge sourceTypeId={type.sourceTypeId} />
                    </div>
                  ))
                ) : (
                  <span>-</span>
                )}
              </div>
            </DetailRow>
            <DetailRow label="Provinsi Transaksi">
              <div className={styles.provinceList}>
                {activeProvinces.length > 0 ? (
                  activeProvinces.map((province) => (
                    <span className={styles.provinceItem} key={province.value || province.label}>
                      {province.label}
                    </span>
                  ))
                ) : (
                  <span>-</span>
                )}
              </div>
            </DetailRow>
          </Stack>
        </div>
        <Flex
          align="center"
          className={styles.actionRow}
          gap="md"
          justify="flex-start"
          mt="lg"
          wrap="wrap"
        >
          <Button
            className={styles.btnAction}
            disabled={disableWhitelist}
            icon={
              <img
                alt=""
                aria-hidden="true"
                className={styles.actionSvgIcon}
                src={icLocation2}
              />
            }
            onClick={handleOpenModal}
            style="blue"
          >
            Batasi Provinsi Transaksi
          </Button>
          <Button
            className={styles.btnAction}
            icon={
              <img
                alt=""
                aria-hidden="true"
                className={styles.actionSvgIcon}
                src={icInfo2}
              />
            }
            onClick={handleGoToDetail}
            outlined
            style="blue"
          >
            Rincian Data
          </Button>
        </Flex>
        {disableWhitelist && (
          <div className={styles.warningNote}>
            <img
              alt=""
              aria-hidden="true"
              className={styles.warningNoteIcon}
              src={icInfoSolid}
            />
            <Text className={styles.warningNoteText}>{disableWhitelistMessage}</Text>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <Card mt={30} radius="md" withBorder>
        <Card.Section
          className={styles.searchSection}
          m="auto"
          maw="44rem"
          p={32}
          px={{ base: 20, md: 200 }}
        >
          <Flex direction={{ base: 'column', md: 'row' }} gap="md">
            <img
              alt="Pembatasan transaksi provinsi"
              height="100"
              src="/assets/illu-setting-merchant.svg"
              width="100"
            />
            <div>
              <Text fw={500} fz={24}>
                Batasi Transaksi Pelanggan Berdasarkan Provinsi
              </Text>
              <Text c="#52575C" fw={400} mb="md" size="lg">
                Cari data NIK pelanggan untuk mengatur pembatasan transaksi
                pelanggan berdasarkan provinsi.
              </Text>
            </div>
          </Flex>
          <CustomerSearchForm
            handleSubmit={handleSubmitSearch}
            isError={
              !dataProvinceTransactionRestriction.success &&
              dataProvinceTransactionRestriction.message
            }
            isLoading={dataProvinceTransactionRestriction.isLoading}
            message={dataProvinceTransactionRestriction.message}
          />
          {dataProvinceTransactionRestriction.success &&
            !dataProvinceTransactionRestriction.isLoading &&
            renderResultSearch()}
        </Card.Section>
      </Card>
      <ProvinceTransactionRestrictionModal
        customerName={searchResult}
        onClose={handleCloseModal}
        onSubmit={handleSubmitRestriction}
        opened={isModalOpen}
        provinceOptions={sortedProvinceOptions}
        provinces={searchResult?.provinces || []}
        submitLoading={updateProvinceTransactionRestriction.isLoading}
      />
    </div>
  );
}

DetailRow.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
};
